import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { setServerErrors, useMutation } from '../../../Shared/api';
import { useLocale } from '../../../Shared/locale';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { Event } from '../../../Shared/types';
import { Button } from '../../../Shared/UI/Button';
import ErrorMessage from '../../../Shared/UI/ErrorMessage';
import InfoMessage from '../../../Shared/UI/InfoMessage';
import { Input } from '../../../Shared/UI/Input';
import InputGroup from '../../../Shared/UI/InputGroup';
import InputLabel from '../../../Shared/UI/InputLabel';
import ServerErrors from '../../../Shared/UI/ServerErrors';
import { useRouter } from '../../router';

export default function CreateProjectForm({
  event,
  campaignsCount,
  onSuccess,
}: {
  event: Event;
  campaignsCount: number;
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const { formatNumber } = useLocale();
  const notifier = useNotifier();

  const { register, handleSubmit, setError, formState: { errors, defaultValues } } = useForm({
    defaultValues: {
      title: event.title,
    },
  });

  const [createProject, { loading, errors: serverErrors }] = useMutation(routes.create_project(event.id), {
    onSuccess: () => {
      notifier.notify(t('backend:project_created'));
      onSuccess?.();
    },
    onError: (errors) => {
      setServerErrors(errors, setError, defaultValues);
    },
  });

  return (
    <form onSubmit={handleSubmit(createProject)}>
      <div className="space-y-6">
        <div className="space-y-2">
          <h3 className="py-1">
            {t('backend:add_a_project')}
          </h3>
          <p className="text-slate-700">
            {t('backend:create_event_project_intro')}
          </p>
        </div>

        <hr />

        {serverErrors && <ServerErrors errors={serverErrors} defaultValues={defaultValues} />}

        <InputGroup>
          <InputLabel required valid={!errors.title} htmlFor="create_project_title">
            {t('backend:project_title')}
          </InputLabel>
          <Input
            {...register('title', { required: true })}
            id="create_project_title"
            className="mr-4"
            aria-invalid={!!errors.title}
          />
          <ErrorMessage attribute={t('backend:project_title')} error={errors.title }/>
        </InputGroup>

        <InfoMessage>
          {t('backend:create_event_project_warning', {
            value: formatNumber(campaignsCount),
            count: campaignsCount,
          })}
        </InfoMessage>

        <hr />

        <div className="text-right">
          <Button
            type="submit"
            disabled={loading}
            loading={loading}
          >
            {t('backend:create_project')}
          </Button>
        </div>
      </div>
    </form>
  );
}
